import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Layout, Eye, Keyboard, Save, RotateCcw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useUserPreferences, defaultPreferences } from '@/providers/UserPreferencesProvider';
import { useDemoSession } from '@/providers/DemoSessionProvider';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
};

export function SettingsUserPreferencesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { preferences, updatePreferences } = useUserPreferences();
  const { userName } = useDemoSession();
  
  const [localSidebarPosition, setLocalSidebarPosition] = useState(preferences.sidebarPosition);
  const [localShowWelcomeDialog, setLocalShowWelcomeDialog] = useState(preferences.showWelcomeDialog);
  const [localDefaultLandingPage, setLocalDefaultLandingPage] = useState(preferences.defaultLandingPage);
  const [localRememberLastPage, setLocalRememberLastPage] = useState(preferences.rememberLastPage);
  const [localKeyboardShortcutsEnabled, setLocalKeyboardShortcutsEnabled] = useState(preferences.keyboardShortcutsEnabled);

  // Sync local state with preferences when they change
  useEffect(() => {
    setLocalSidebarPosition(preferences.sidebarPosition);
    setLocalShowWelcomeDialog(preferences.showWelcomeDialog);
    setLocalDefaultLandingPage(preferences.defaultLandingPage);
    setLocalRememberLastPage(preferences.rememberLastPage);
    setLocalKeyboardShortcutsEnabled(preferences.keyboardShortcutsEnabled);
  }, [preferences]);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    localSidebarPosition !== preferences.sidebarPosition ||
    localShowWelcomeDialog !== preferences.showWelcomeDialog ||
    localDefaultLandingPage !== preferences.defaultLandingPage ||
    localRememberLastPage !== preferences.rememberLastPage ||
    localKeyboardShortcutsEnabled !== preferences.keyboardShortcutsEnabled;

  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const handleSave = () => {
    const changes: string[] = [];
    if (localSidebarPosition !== preferences.sidebarPosition)
      changes.push(`Sidebar: ${localSidebarPosition}`);
    if (localShowWelcomeDialog !== preferences.showWelcomeDialog)
      changes.push(`Welcome Dialog: ${localShowWelcomeDialog ? 'On' : 'Off'}`);
    if (localDefaultLandingPage !== preferences.defaultLandingPage)
      changes.push(`Landing Page: ${localDefaultLandingPage}`);
    if (localRememberLastPage !== preferences.rememberLastPage)
      changes.push(`Remember Last Page: ${localRememberLastPage ? 'On' : 'Off'}`);
    if (localKeyboardShortcutsEnabled !== preferences.keyboardShortcutsEnabled)
      changes.push(`Keyboard Shortcuts: ${localKeyboardShortcutsEnabled ? 'On' : 'Off'}`);
    updatePreferences({
      sidebarPosition: localSidebarPosition,
      showWelcomeDialog: localShowWelcomeDialog,
      defaultLandingPage: localDefaultLandingPage,
      rememberLastPage: localRememberLastPage,
      keyboardShortcutsEnabled: localKeyboardShortcutsEnabled,
    });
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'User Preferences',
      newValue: changes.join('; ') || 'No visible changes',
      detail: 'Updated via Settings > User Preferences',
    });
    toast.success('User preferences saved successfully');
  };

  const handleDiscard = () => {
    setLocalSidebarPosition(preferences.sidebarPosition);
    setLocalShowWelcomeDialog(preferences.showWelcomeDialog);
    setLocalDefaultLandingPage(preferences.defaultLandingPage);
    setLocalRememberLastPage(preferences.rememberLastPage);
    setLocalKeyboardShortcutsEnabled(preferences.keyboardShortcutsEnabled);
    toast.info('Changes discarded');
  };

  const handleResetToDefaults = () => {
    setLocalSidebarPosition(defaultPreferences.sidebarPosition);
    setLocalShowWelcomeDialog(defaultPreferences.showWelcomeDialog);
    setLocalDefaultLandingPage(defaultPreferences.defaultLandingPage);
    setLocalRememberLastPage(defaultPreferences.rememberLastPage);
    setLocalKeyboardShortcutsEnabled(defaultPreferences.keyboardShortcutsEnabled);
    toast.info('Reset to default preferences');
  };

  const landingPageOptions = [
    { id: 'homepage', name: 'Homepage' },
    { id: 'timesheet', name: 'Timesheet' },
    { id: 'approvals', name: 'Approvals' },
    { id: 'projects', name: 'Projects' },
  ];

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto space-y-8 p-6 pb-32" data-tour="settings-user-preferences-display">
          {/* Greeting */}
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">{getGreeting()}, {userName}</h2>
            <p className="text-muted-foreground">
              Customise Projects & Billing to fit your preferences
            </p>
          </div>

          {hasUnsavedChanges && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleDiscard}
                size="sm"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Discard Changes
              </Button>
              <Button
                onClick={handleSave}
                size="sm"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Preferences
              </Button>
            </div>
          )}

          {/* Appearance Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Layout className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Appearance</h3>
                <p className="text-sm text-muted-foreground">Customise the look and feel of your interface</p>
              </div>
            </div>
            
            <div className="rounded-lg border bg-card">
              <div className="divide-y">
                {/* 
                  HIDDEN: Sidebar Position Toggle
                  
                  Product Decision: This feature has been temporarily hidden from the UI.
                  
                  Reason: There is a known bug where saving the sidebar position to 'right' 
                  has no effect - the sidebar always remains on the left regardless of the 
                  saved preference. Rather than removing the code entirely, we've hidden the 
                  UI element so it can be easily restored if/when this feature is properly 
                  supported in the future.
                  
                  The underlying code and state management (localSidebarPosition, 
                  setLocalSidebarPosition, useUserPreferences hook) remain intact.
                  
                  To restore: Uncomment the JSX block below.
                  
                  <div className="flex items-center justify-between p-4">
                    <div className="space-y-0.5 flex-1">
                      <Label htmlFor="sidebar-position" className="text-sm font-medium">
                        Sidebar Position
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Choose which side the navigation sidebar appears
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${localSidebarPosition === 'left' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        Left
                      </span>
                      <Switch
                        id="sidebar-position"
                        checked={localSidebarPosition === 'right'}
                        onCheckedChange={(checked) => setLocalSidebarPosition(checked ? 'right' : 'left')}
                      />
                      <span className={`text-xs ${localSidebarPosition === 'right' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        Right
                      </span>
                    </div>
                  </div>
                */}


              </div>
            </div>
          </section>

          {/* 
            HIDDEN: Navigation Section (Default Landing Page & Remember Last Page)
            
            Product Decision: This entire section has been temporarily hidden from the UI.
            
            Reason: Both the "Default Landing Page" and "Remember Last Visited Page" options
            are believed to not be working correctly. Rather than removing the code entirely,
            we've hidden the UI elements so they can be easily restored if/when these features
            are properly supported in the future.
            
            The underlying code and state management (localDefaultLandingPage, 
            setLocalDefaultLandingPage, localRememberLastPage, setLocalRememberLastPage,
            landingPageOptions, useUserPreferences hook) remain intact.
            
            To restore: Uncomment the JSX block below.
            
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Home className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Navigation</h3>
                  <p className="text-sm text-muted-foreground">Control your navigation and startup behaviour</p>
                </div>
              </div>
              
              <div className="rounded-lg border bg-card">
                <div className="divide-y">
                  <div className="flex items-center justify-between p-4">
                    <div className="space-y-0.5 flex-1">
                      <Label htmlFor="landing-page" className="text-sm font-medium">
                        Default Landing Page
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Choose which page opens when you log in
                      </p>
                    </div>
                    <Select
                      value={localDefaultLandingPage}
                      onValueChange={setLocalDefaultLandingPage}
                    >
                      <SelectTrigger id="landing-page" className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {landingPageOptions.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-4">
                    <div className="space-y-0.5 flex-1">
                      <Label htmlFor="remember-page" className="text-sm font-medium cursor-pointer">
                        Remember Last Visited Page
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Automatically return to your last visited page on login (overrides default landing page)
                      </p>
                    </div>
                    <Checkbox
                      id="remember-page"
                      checked={localRememberLastPage}
                      onCheckedChange={(checked) => setLocalRememberLastPage(checked === true)}
                    />
                  </div>
                </div>
              </div>
            </section>
          */}

          {/* Accessibility Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Keyboard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Accessibility</h3>
                <p className="text-sm text-muted-foreground">Keyboard shortcuts and interaction preferences</p>
              </div>
            </div>
            
            <div className="rounded-lg border bg-card">
              <div className="divide-y">
                {/* Keyboard Shortcuts */}
                <div className="flex items-center justify-between p-4">
                  <div className="space-y-0.5 flex-1">
                    <Label htmlFor="keyboard-shortcuts" className="text-sm font-medium cursor-pointer">
                      Keyboard Shortcuts
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Enable keyboard shortcuts for faster navigation and accessibility
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Press <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">Ctrl+/</kbd> to view all shortcuts
                    </p>
                  </div>
                  <Checkbox
                    id="keyboard-shortcuts"
                    checked={localKeyboardShortcutsEnabled}
                    onCheckedChange={(checked) => setLocalKeyboardShortcutsEnabled(checked === true)}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* General Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Eye className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">General</h3>
                <p className="text-sm text-muted-foreground">General application preferences</p>
              </div>
            </div>
            
            <div className="rounded-lg border bg-card">
              <div className="divide-y">
                {/* Welcome Dialog */}
                <div className="flex items-center justify-between p-4">
                  <div className="space-y-0.5 flex-1">
                    <Label htmlFor="show-welcome" className="text-sm font-medium cursor-pointer">
                      Show Welcome Dialog
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Display the welcome message when the application starts
                    </p>
                  </div>
                  <Checkbox
                    id="show-welcome"
                    checked={localShowWelcomeDialog}
                    onCheckedChange={(checked) => setLocalShowWelcomeDialog(checked === true)}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reset to Defaults */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              size="sm"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsUserPreferencesPage;
